import React, { useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Button, Form } from 'semantic-ui-react';
import { each, isEmpty, chain, values } from 'lodash';
import useForm from 'react-hook-form';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import PersonalInfo from '../../components/FormComponents/PersonalInfo/PersonalInfo';
import PassportIdInfo from '../../components/FormComponents/PassportIdInfoWrapper/PassportIdInfo';
import ContactInfo from '../../components/FormComponents/ContactInfo/ContactInfo';
import mapBackendErrorsToUserFields from '../../validations/backendErrors';

import { getCountries, getStates } from '../../redux/modules/countries';
import { mainUserFormSchema } from '../../validations/userFormValidations';

import { updateUser } from '../../redux/modules/user';
import useSocCardStatus from '../../hooks/useSocCardStatus';
import FormSection from '../../components/FormComponents/FormSection';
import { ReactComponent as PersonalInfoIcon } from '../../img/personal_info_icon.svg';
import { ReactComponent as PassportIdIcon } from '../../img/passport_id_icon.svg';
import { ReactComponent as ContactIcon } from '../../img/contact_info_icon.svg';
import getFormattedFields from '../../helpers/formatPassportInfo';
import commonStyles from '../Registration/userFormCommonStyles.module.scss';

const formSchema = mainUserFormSchema;

const UserDetailsForm = () => {
  const { t } = useTranslation();
  const user = useSelector((store) => store.userReducer.user, shallowEqual);

  const dispatch = useDispatch();
  const loadingUser = useSelector((store) => store.userReducer.loadingUser);
  const countriesLoading = useSelector((store) => store.countriesReducer.countriesLoading);
  const statesLoading = useSelector((store) => store.countriesReducer.statesLoading);

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getStates());
  }, [dispatch]);

  const {
    register,
    setValue,
    errors,
    triggerValidation,
    handleSubmit,
    setError,
    clearError,
    getValues,
    watch,
    reset,
  } = useForm({
    validationSchema: formSchema,
    mode: 'onBlur',
  });

  const [socCardEnabled, citizenshipUpdate] = useSocCardStatus();

  const onSubmit = (formData) => {
    if (!isEmpty(errors)) {
      return;
    }
    const formattedFields = getFormattedFields(formData);
    const userUpdate = {
      resident: 1, // set residency true by default
      email: user.email,
      ...formData,
      ...formattedFields,
    };

    dispatch(updateUser(userUpdate, user.id)).catch((error) => {
      let backendErrors = values(error.response.data.errors);
      backendErrors = chain(backendErrors)
        .flatten()
        .uniq()
        .value();
      const errorMap = mapBackendErrorsToUserFields(backendErrors);
      each(errorMap, (e, key) => {
        setError(key, 'backend', `backendErrors.${e}`);
      });
    });
  };

  return (
    <div id={commonStyles.form}>
      <h2 className={cn('header', commonStyles.header, commonStyles.mobileContent)}>
        {t('personalData.header')}
      </h2>
      <Form
        className={commonStyles.userForm}
        loading={loadingUser || statesLoading || countriesLoading}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormSection title="personalInfo.header" icon={<PersonalInfoIcon />}>
          <PersonalInfo
            initialData={user}
            formProps={{
              register,
              setValue,
              getValues,
              errors,
              triggerValidation,
              clearError,
              reset,
            }}
          />
        </FormSection>
        <FormSection title="passportIdInfo.header" icon={<PassportIdIcon />}>
          <PassportIdInfo
            initialData={user}
            onCitizenshipChange={citizenshipUpdate}
            socCardEnabled={socCardEnabled}
            formProps={{
              register,
              setValue,
              getValues,
              errors,
              triggerValidation,
              clearError,
            }}
          />
        </FormSection>
        <FormSection title="contactInfo.header" icon={<ContactIcon />} bottomBorder>
          <ContactInfo
            initialData={user}
            formProps={{
              register,
              getValues,
              setValue,
              errors,
              triggerValidation,
              clearError,
              watch,
            }}
            hasPhone
          />
        </FormSection>

        <div className={commonStyles.buttonSection}>
          <Button className={commonStyles.submitButton} type="submit">
            {t('login.submit')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UserDetailsForm;
